import React from "react";
import { Box, Typography, Divider, Button, Chip } from "@material-ui/core";
import exerciseConstants from "constants/Exercises/exerciseConstants";

const COLORS = {
  error: {
    background: "#fff5f5",
    border: "#f44336",
    title: "#f44336",
    text: "#d32f2f",
  },
  success: {
    background: "#f0f7ff",
    border: "#2196f3",
    title: "#1976d2",
  },
  failureButton: {
    background: "#f44336",
    text: "#ffffff",
  },
  falsePositive: {
    border: "#f44336",
    text: "#f44336",
  },
  falseNegative: {
    border: "#e65100",
    text: "#e65100",
  },
};

/**
 * Exibe o resultado da validação de uma tarefa.
 * Este componente trata diferentes cenários de resposta:
 * - Erros de processamento (ex: erro de gramática ou backend)
 * - Sucesso com estatísticas de execução
 * - Exibição de falhas detalhadas:
 *    - Falsos positivos (palavras aceitas incorretamente)
 *    - Falsos negativos (palavras rejeitadas incorretamente)
 *
 * Funcionalidades:
 * - Exibição de métricas (testes passados e rating)
 * - Alternância dinâmica da visualização de falhas
 * - Interação com palavras (click → callback externo)
 * - Suporte a múltiplos idiomas
 *
 * @typedef {Object} TaskResponseData
 * @property {string} [errorMessage] - Mensagem de erro retornada pelo backend
 * @property {number} passedTests - Número de testes aprovados
 * @property {number} totalTests - Número total de testes
 * @property {number} rating - Taxa de acerto (0 a 1)
 * @property {string[]} [falsePositives] - Palavras aceitas incorretamente
 * @property {string[]} [falseNegatives] - Palavras rejeitadas incorretamente
 *
 * @param {Object} props
 * @param {TaskResponseData|null} props.responseData - Resultado da validação
 * @param {"pt"|"en"} props.language - Idioma da interface
 * @param {(word: string) => void} [props.onWordClick] - Callback ao clicar em uma palavra
 *
 * @returns {JSX.Element|null}
 *
 * @example
 * <TaskResponse
 *   responseData={response}
 *   language="pt"
 *   onWordClick={(word) => simulate(word)}
 * />
 * @author Willian
 */

export default function TaskResponse({ responseData, language, onWordClick }) {
  const [showFailed, setShowFailed] = React.useState(false);
  const labels = exerciseConstants.response;

  if (!responseData) {
    return null;
  }

  // =========================
  // HELPERS
  // =========================

  const formatWord = (word) => {
    if (word === "") {
      return "λ";
    }
    return word;
  };

  const getFalsePositives = () => {
    if (responseData.falsePositives) {
      return responseData.falsePositives;
    }
    return null;
  };

  const getFalseNegatives = () => {
    if (responseData.falseNegatives) {
      return responseData.falseNegatives;
    }
    return null;
  };

  const hasFailures = () => {
    const fp = getFalsePositives();
    const fn = getFalseNegatives();
    if (fp.length > 0) {
      return true;
    }
    if (fn.length > 0) {
      return true;
    }
    return false;
  };

  const handleToggleFailed = () => {
    setShowFailed(!showFailed);
  };

  const renderChips = (words, color, prefix) => {
    const chips = [];

    for (let i = 0; i < words.length; i++) {
      const word = words[i];

      const handleClick = () => {
        if (onWordClick) {
          onWordClick(word);
        }
      };

      chips.push(
        <Chip
          key={prefix + "-" + i}
          label={formatWord(word)}
          size="small"
          onClick={handleClick}
          style={{
            cursor: "pointer",
            border: "1px solid " + color.border,
            color: color.text,
            backgroundColor: "white",
          }}
        />
      );
    }

    return chips;
  };

  const renderSummary = () => {
    return (
      <>
        <Typography
          variant="subtitle2"
          style={{
            color: COLORS.success.title,
            fontWeight: "bold",
          }}
        >
          {labels.successTitle[language]}
        </Typography>
        <Divider style={{ margin: "8px 0" }} />
        <Box display="flex" justifyContent="space-between" mb={1}>
          <Typography variant="caption">
            {labels.testsPassed[language]}{" "}
            <strong>
              {responseData.passedTests} / {responseData.totalTests}
            </strong>
          </Typography>

          <Typography variant="caption">
            {labels.rating[language]}{" "}
            <strong>{Math.round(responseData.rating * 100)}%</strong>
          </Typography>
        </Box>
      </>
    );
  };

  // =========================
  // ERROR CASE
  // =========================

  if (responseData.errorMessage) {
    return (
      <Box
        mt={2}
        mb={2}
        p={1.5}
        style={{
          backgroundColor: COLORS.error.background,
          borderRadius: "4px",
          borderLeft: "4px solid " + COLORS.error.border,
        }}
      >
        <Typography
          variant="subtitle2"
          style={{
            color: COLORS.error.title,
            fontWeight: "bold",
          }}
        ></Typography>
      </Box>
    );
  }

  // =========================
  // FAILURES SECTION
  // =========================

  let failuresSection = null;

  if (hasFailures()) {
    let failedContentBox = null;

    if (showFailed) {
      const falsePositives = getFalsePositives();
      const falseNegatives = getFalseNegatives();
      let falsePositivesBox = null;
      if (falsePositives.length > 0) {
        falsePositivesBox = (
          <Box mb={2}>
            <Typography
              variant="caption"
              style={{
                color: COLORS.falsePositive.text,
                fontWeight: "bold",
                display: "block",
              }}
            >
              {labels.falsePositivesTitle[language]}
            </Typography>

            <Box
              display="flex"
              flexWrap="wrap"
              style={{ gap: "4px", marginTop: "4px" }}
            >
              {renderChips(falsePositives, COLORS.falsePositive, "fp")}
            </Box>
          </Box>
        );
      }

      let falseNegativesBox = null;
      if (falseNegatives.length > 0) {
        falseNegativesBox = (
          <Box>
            <Typography
              variant="caption"
              style={{
                color: COLORS.falseNegative.text,
                fontWeight: "bold",
                display: "block",
              }}
            >
              {labels.falseNegativesTitle[language]}
            </Typography>

            <Box
              display="flex"
              flexWrap="wrap"
              style={{ gap: "4px", marginTop: "4px" }}
            >
              {renderChips(falseNegatives, COLORS.falseNegative, "fn")}
            </Box>
          </Box>
        );
      }

      failedContentBox = (
        <Box
          mt={1}
          p={1}
          style={{
            backgroundColor: "rgba(255,255,255,0.7)",
            borderRadius: "4px",
          }}
        >
          {falsePositivesBox}
          {falseNegativesBox}
        </Box>
      );
    }

    failuresSection = (
      <Box mt={1}>
        <Button
          variant="contained"
          size="small"
          fullWidth
          onClick={handleToggleFailed}
          style={{
            fontSize: "11px",
            backgroundColor: COLORS.failureButton.background,
            color: COLORS.failureButton.text,
            fontWeight: "bold",
            marginBottom: "8px",
          }}
        >
          {labels.showFailed[language]}
        </Button>

        {failedContentBox}
      </Box>
    );
  }

  // =========================
  // FINAL RENDER
  // =========================

  return (
    <Box
      mt={2}
      mb={2}
      p={1.5}
      style={{
        backgroundColor: COLORS.success.background,
        borderRadius: "4px",
        borderLeft: "4px solid " + COLORS.success.border,
      }}
    >
      {renderSummary()}
      {failuresSection}
    </Box>
  );
}
